/* src/contexts/customGame/CustomGameContext.js */
import React, { createContext, useContext, useReducer, useCallback, useRef } from 'react';
// Import Firebase functions and the app instance
import { getFunctions, httpsCallable } from 'firebase/functions'; // HttpsCallableError removed
import { app as firebaseApp } from '../../firebaseConfig'; // Import 'app' and alias it to firebaseApp

// Initialize Firebase Functions service client-side
const functionsInstance = getFunctions(firebaseApp); // Use the imported app instance


// --- Helper to get default configuration structure ---
const getInitialDefaultConfig = () => ({
    presetMetadata: {
        name: "Default Custom Game",
        customGameVersion: "1.0.0",
    },
    settings: {
        Session: {
            NValue: { Mode: "Fixed", FixedValue: 2 },
            Termination: { Mode: "Fixed Trial Count", TrialCount: 20 },
            RandomSeed: { Mode: "Automatic" },
        },
        Stimulus: { EnabledTypes: ["Audio", "Position"] },
        Sequence: { Matches: { TargetPercentage: 0.25 } },
        Timing: { StimulusDuration: { FixedDurationMs: 500 }, ISI: { FixedDurationMs: 2500 } },
    }
});


// --- Initial State for the Reducer ---
const initialGameState = {
    config: getInitialDefaultConfig(),
    gameState: 'idle',
    currentTrial: -1,
    totalTrials: 0,
    sequence: [],
    responses: {},
    score: 0,
    gameResults: null,
    error: null,
    isLoadingSequence: false,
};

// --- Reducer Function ---
function customGameReducer(state, action) {
    switch (action.type) {
        case 'SET_CONFIG':
            return {
                ...initialGameState,
                config: action.payload,
                gameState: 'idle',
            };
        case 'LOAD_PRESET_CONFIG':
             return {
                ...initialGameState,
                config: action.payload,
                gameState: 'idle',
            };
        case 'START_SEQUENCE_GENERATION':
            return {
                ...state,
                gameState: 'preparing_sequence',
                isLoadingSequence: true,
                error: null,
                sequence: [],
                totalTrials: 0,
                currentTrial: -1,
                gameResults: null,
                responses: {},
            };
        case 'SEQUENCE_GENERATED_SUCCESS':
            return {
                ...state,
                isLoadingSequence: false,
                sequence: action.payload.sequence,
                totalTrials: action.payload.totalTrials || action.payload.sequence.length,
                error: null,
            };
        case 'SEQUENCE_GENERATION_FAILURE':
            return {
                ...state,
                gameState: 'idle',
                isLoadingSequence: false,
                error: action.payload,
                sequence: [],
                totalTrials: 0,
            };
        case 'START_COUNTDOWN':
             return { ...state, gameState: 'countdown' };
        case 'START_PLAYING':
            return {
                ...state,
                gameState: 'playing',
                currentTrial: 0,
                responses: {},
                score: 0,
                gameResults: null,
            };
        case 'PAUSE_GAME':
            return { ...state, gameState: 'paused' };
        case 'RESUME_GAME':
            return { ...state, gameState: 'playing' };
        case 'END_GAME_SUCCESS':
            return {
                ...state,
                gameState: 'results',
                gameResults: action.payload.results,
            };
        case 'RESET_GAME_FOR_NEW_SESSION':
            return {
                ...state,
                gameState: 'idle',
                currentTrial: -1,
                sequence: [],
                responses: {},
                score: 0,
                gameResults: null,
                error: null,
                isLoadingSequence: false,
            };
        default:
            return state;
    }
}


// --- Create Context ---
const CustomGameContext = createContext();

// --- Context Provider Component ---
export const CustomGameProvider = ({ children }) => {
    const [state, dispatch] = useReducer(customGameReducer, initialGameState);
    const editableConfigRef = useRef(getInitialDefaultConfig());

    const setCustomGameConfig = useCallback((newConfig) => {
        editableConfigRef.current = newConfig;
        dispatch({ type: 'SET_CONFIG', payload: newConfig });
        console.log('[CustomGameContext] Config set in context state and ref:', newConfig);
    }, []);

    const getCustomGameConfig = useCallback(() => {
        return editableConfigRef.current;
    }, []);


    const prepareCustomGameSequence = useCallback(async () => {
        const configToPlay = state.config;

        if (!configToPlay || !configToPlay.settings) {
            console.error('[CustomGameContext] No valid config in state to generate sequence.');
            dispatch({ type: 'SEQUENCE_GENERATION_FAILURE', payload: 'Configuration is missing or invalid in context state.' });
            return;
        }
        dispatch({ type: 'START_SEQUENCE_GENERATION' });

        try {
            console.log('[CustomGameContext] Calling Firebase function "generateCustomGameSequence" with settings:', configToPlay.settings);
            // Use the initialized functionsInstance
            const generateSequenceFn = httpsCallable(functionsInstance, 'generateCustomGameSequence');
            
            const result = await generateSequenceFn({ configSettings: configToPlay.settings });
            
            const sequenceData = result.data;

            console.log('[CustomGameContext] Received from Firebase function:', sequenceData);

            if (!sequenceData || !Array.isArray(sequenceData.sequence) || typeof sequenceData.totalTrials !== 'number') {
                console.error('[CustomGameContext] Invalid sequence data structure received:', sequenceData);
                throw new Error('Invalid sequence data received from server.');
            }

            dispatch({
                type: 'SEQUENCE_GENERATED_SUCCESS',
                payload: {
                    sequence: sequenceData.sequence,
                    totalTrials: sequenceData.totalTrials,
                }
            });
            dispatch({ type: 'START_COUNTDOWN' });

        } catch (error) {
            console.error('[CustomGameContext] Error calling "generateCustomGameSequence":', error);
            let errorMessage = 'Failed to generate game sequence.';
            // Check for Firebase Functions error structure
            if (error.code && error.message) {
                errorMessage = `Error: ${error.message} (Code: ${error.code})`;
            } else if (error.message) {
                errorMessage = error.message; // Generic JS error
            }
            dispatch({ type: 'SEQUENCE_GENERATION_FAILURE', payload: errorMessage });
        }
    }, [state.config]);

    const startCountdown = useCallback(() => {
        if (state.gameState === 'idle' && state.sequence && state.sequence.length > 0 && !state.isLoadingSequence && !state.error) {
             dispatch({ type: 'START_COUNTDOWN' });
        } else {
            console.warn('[CustomGameContext] startCountdown called in inappropriate state or sequence not ready.');
        }
    }, [state.gameState, state.sequence, state.isLoadingSequence, state.error]);

    const startGamePlay = useCallback(() => {
        if (state.gameState === 'countdown') {
            dispatch({ type: 'START_PLAYING' });
        } else {
            console.warn('[CustomGameContext] startGamePlay called in inappropriate state.');
        }
    }, [state.gameState]);

    const pauseCustomGame = useCallback(() => {
        if (state.gameState === 'playing') {
            dispatch({ type: 'PAUSE_GAME' });
        }
    }, [state.gameState]);

    const resumeCustomGame = useCallback(() => {
        if (state.gameState === 'paused') {
            dispatch({ type: 'RESUME_GAME' });
        }
    }, [state.gameState]);

    const endCustomGame = useCallback((resultsData) => {
        console.log('[CustomGameContext] Custom game ended. Results data:', resultsData);
        const processedResults = resultsData || { message: "Session ended." };
        dispatch({ type: 'END_GAME_SUCCESS', payload: { results: processedResults } });
    }, []);

    const resetForNewSession = useCallback(() => {
        dispatch({ type: 'RESET_GAME_FOR_NEW_SESSION' });
    }, []);


    const contextValue = {
        config: state.config,
        setCustomGameConfig,
        getCustomGameConfig,

        gameState: state.gameState,
        currentTrial: state.currentTrial,
        totalTrials: state.totalTrials,
        sequence: state.sequence,
        gameResults: state.gameResults,
        error: state.error,
        isLoadingSequence: state.isLoadingSequence,

        prepareCustomGameSequence,
        startCountdown,
        startGamePlay,
        pauseCustomGame,
        resumeCustomGame,
        endCustomGame,
        resetForNewSession,
    };

    return (
        <CustomGameContext.Provider value={contextValue}>
            {children}
        </CustomGameContext.Provider>
    );
};

export const useCustomGame = () => {
    const context = useContext(CustomGameContext);
    if (context === undefined) {
        throw new Error('useCustomGame must be used within a CustomGameProvider');
    }
    return context;
};